/**
 * Topbar — sticky top bar: Suche links, Glocke + „Hochladen" rechts.
 *
 * Redesign S2 (2026-10-10, `tasks/design-handoff-redesign-s2.md` Entscheide 1+2):
 * Upload und Notifications-Glocke sind zurück in der Topbar (2026-07-19 bis S2
 * lebten sie in der Sidebar, die Bar war randlos das Suchfeld). Die Suche
 * bleibt das dominante Element (links, begrenzt auf 640px, ⌘K-Hinweis);
 * „Hochladen" ist der EINE gelbe CTA pro Screen (`Button variant="cta"`).
 * Avatar/Account bleibt unten in der Sidebar (`SidebarAccount`).
 *
 * - Sticky `top-0 z-30 backdrop-blur-md` (Z-Index-Hierarchie: Drawer z-50,
 *   Backdrop z-40, Topbar z-30, CardActions z-20).
 * - Hamburger ist OPEN-ONLY: Click ruft `setSidebarOpen(true)`; close paths sind
 *   Backdrop-click / Esc / Auto-close on nav.
 * - Innerer Wrapper max-w-[1320px] zentriert → fluchtet mit dem Seiten-Content.
 * - `data-tour="upload-button"` (Onboarding-Tour, `lib/onboarding-tour.ts`) zieht
 *   mit dem CTA hierher — fehlt der Anker, filtert die Tour den Step still weg.
 * - Mobile (<sm): CTA nur als Icon (aria-label), damit die Suche Platz behält.
 */
import { I } from '../lib/icons';
import { useSidebarState } from '../lib/layout-context';
import { useUploadModal } from '../lib/upload-context';
import { GlobalSearch } from './GlobalSearch';
import { NotificationsBell } from './NotificationsBell';
import { Button } from './ui/Button';

export function Topbar() {
  const { sidebarOpen, setSidebarOpen, hamburgerRef } = useSidebarState();
  // useUploadModal no-op't außerhalb des Providers (isolierte Tests).
  const { open: openUploadModal } = useUploadModal();

  return (
    <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-ink-100">
      <div className="flex items-center h-16 px-4 md:px-9 gap-2 md:gap-3 mx-auto max-w-[1320px]">
        {/* Hamburger — Mobile only, open-only trigger */}
        <button
          ref={hamburgerRef}
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Menü öffnen"
          aria-expanded={sidebarOpen}
          aria-controls="app-sidebar"
          className="md:hidden flex items-center justify-center w-11 h-11 -ml-2 text-ink-700 hover:bg-ink-100 rounded-control transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
        >
          <I.menu size={20} />
        </button>

        {/* App-weite Suche — links, begrenzt; der Rest der Breite ist Luft. */}
        <div className="flex flex-1 min-w-0">
          <GlobalSearch />
        </div>

        <NotificationsBell />

        <Button
          variant="cta"
          onClick={() => openUploadModal()}
          data-tour="upload-button"
          leadingIcon={<I.upload size={16} weight="bold" />}
          aria-label="Hochladen"
          className="h-11 px-3 sm:px-4"
        >
          <span className="hidden sm:inline">Hochladen</span>
        </Button>
      </div>
    </div>
  );
}
